import React, { useEffect, useMemo, useRef, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';

import ClearIcon from '@mui/icons-material/Clear';
import ImageIcon from '@mui/icons-material/Image';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { Button, GetListParams, RaRecord, useDataProvider } from 'react-admin';
import { SELECTED_CHAIN_KEY } from '../../constants';
import getChainName from '../../lib/getChainName';
import { getRetailerId, isUserRetailer } from '../../lib';
import { Box, Typography } from '@mui/material';

const ALL_CHAINS_OPTION = '__all__';

const normalize = (value: string) =>
  value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const ChainButton: React.FC = () => {

  const dataProvider = useDataProvider();

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [chains, setChains] = useState<RaRecord[]>([]);
  const [search, setSearch] = useState<string>('');
  const [highlight, setHighlight] = useState<number>(0);
  const [chainName, setChainName] = useState<string | null>(getChainName());

  const open = Boolean(anchorEl);

  useEffect(() => {
    const filter: { retailerId?: string } = {};
    if (isUserRetailer()) { filter.retailerId = getRetailerId(); }

    dataProvider.getList('chains', { filter, sort: { field: 'name', order: 'ASC' } } as GetListParams)
      .then(({ data }) => {
        if (data) {
          setChains(data)
        }
      })
      .catch(error => {
        console.log(error)
      })
  }, []);

  const handleClickOpen = () => {
    setSearch('');
    setHighlight(0);
    setAnchorEl(anchorRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChainClick = (chain) => {
    // Chave própria, nunca o USER_KEY: o authManager reescreve o perfil inteiro a cada
    // renovação de token e levaria a escolha junto.
    localStorage.setItem(SELECTED_CHAIN_KEY, JSON.stringify({
      chainId: chain.id,
      chainName: chain.name,
      chainUseQrcode: chain.useQrcode,
      chainUsePromoter: chain.usePromoter,
    }));
    setChainName(chain.name);
    handleClose();
    window.location.reload();
  };

  const handleDefaultChainClick = () => {
    localStorage.removeItem(SELECTED_CHAIN_KEY);
    setChainName("")
    handleClose();
    window.location.reload();
  }

  const filteredChains = useMemo(() => {
    const term = normalize(search);
    if (!term) { return chains; }
    return chains.filter((chain) => normalize(chain.name || '').includes(term));
  }, [chains, search]);

  // "TODAS" entra como primeira opção da lista para a navegação por teclado
  const options = useMemo(
    () => (filteredChains.length ? [ALL_CHAINS_OPTION, ...filteredChains] : []),
    [filteredChains],
  );

  const selectOption = (option) => {
    if (option === ALL_CHAINS_OPTION) {
      handleDefaultChainClick();
      return;
    }
    handleChainClick(option);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!options.length) { return; }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((current) => (current + 1) % options.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((current) => (current - 1 + options.length) % options.length);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      selectOption(options[highlight] ?? options[0]);
    }
  };

  useEffect(() => {
    const node = listRef.current?.querySelector('[data-highlighted="true"]');
    node?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  return (
    <>
      <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center' }}>
        <Button sx={{ color: 'white', padding: 1.5 }} label={chainName || 'Selecione uma rede'} onClick={handleClickOpen} />
        {chainName && (
          <IconButton
            size="small"
            onClick={handleDefaultChainClick}
            sx={{ color: 'white', ml: -1 }}
            title="Limpar seleção"
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 340, maxWidth: '90vw', mt: 0.5, overflow: 'hidden' },
          },
        }}
      >
        <Box sx={{ p: 1.5, pb: 1 }} onKeyDown={handleKeyDown}>
          <TextField
            autoFocus
            size="small"
            value={search}
            onChange={(event) => { setSearch(event.target.value); setHighlight(0); }}
            placeholder="Pesquisar rede"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <List ref={listRef} sx={{ maxHeight: 320, overflowY: 'auto', mt: 0.5, py: 0 }}>
            {
              options.length ?
                options.map((option, index) => (
                  typeof option === 'string' ?
                    <ListItemButton
                      key={ALL_CHAINS_OPTION}
                      data-highlighted={index === highlight}
                      selected={index === highlight}
                      sx={{ py: 0.5, px: 1 }}
                      onClick={handleDefaultChainClick}
                    >
                      <ListItemText style={{ marginLeft: 10 }} primary="TODAS" />
                    </ListItemButton>
                    :
                    <ListItemButton
                      key={option.id}
                      data-highlighted={index === highlight}
                      selected={index === highlight}
                      sx={{ py: 0.5, px: 1 }}
                      onClick={() => handleChainClick(option)}
                    >
                      {
                        option.logoPath ?
                          <Avatar alt={option.name} src={option.logoPath} sx={{ width: 32, height: 32 }} />
                          :
                          <Avatar sx={{ width: 32, height: 32 }}>
                            <ImageIcon fontSize="small" />
                          </Avatar>
                      }
                      <ListItemText style={{ marginLeft: 10 }} primary={option.name} />
                    </ListItemButton>
                ))
                :
                <Typography sx={{ px: 1, py: 1 }}>Nenhuma rede encontrada</Typography>
            }
          </List>
        </Box>
      </Popover>
    </>
  );
}

export default ChainButton;
